import { useMemo, useState } from "react";
import type { ProviderBooking } from "../../lib/api";
import { formatPricingAmount } from "../../lib/provider-pricing";
import { useAppPreferences, type TranslationFunction } from "../../lib/preferences";
import {
  formatBookingSchedule,
  getBookingStatusLabel,
  getBookingStatusTone,
  getOperationalContact,
  isActionableBookingStatus,
} from "./booking-utils";

type SortColumn = "start" | "customer" | "unit" | "status" | "total";
type SortDirection = "asc" | "desc";

interface BookingListViewProps {
  bookings: ProviderBooking[];
  loading?: boolean;
  selectedBookingId: string | null;
  onSelectBooking: (bookingId: string) => void;
}

interface ListSort {
  column: SortColumn;
  direction: SortDirection;
}

function filterBookings(bookings: ProviderBooking[], search: string, t: TranslationFunction) {
  if (!search.trim()) {
    return bookings;
  }

  const term = search.trim().toLowerCase();
  return bookings.filter(
    (booking) =>
      booking.customer_name.toLowerCase().includes(term) ||
      booking.unit_name.toLowerCase().includes(term) ||
      booking.status.toLowerCase().includes(term) ||
      t(getBookingStatusLabel(booking.status)).toLowerCase().includes(term) ||
      (booking.customer_email?.toLowerCase().includes(term) ?? false) ||
      (booking.customer_phone?.toLowerCase().includes(term) ?? false),
  );
}

function sortBookings(bookings: ProviderBooking[], sort: ListSort) {
  const direction = sort.direction === "asc" ? 1 : -1;

  return [...bookings].sort((left, right) => {
    let comparison = 0;

    switch (sort.column) {
      case "start":
        comparison = left.scheduled_start_at.localeCompare(right.scheduled_start_at);
        break;
      case "customer":
        comparison = left.customer_name.localeCompare(right.customer_name);
        break;
      case "unit":
        comparison = left.unit_name.localeCompare(right.unit_name);
        break;
      case "status":
        comparison = left.status.localeCompare(right.status);
        break;
      case "total":
        comparison = left.scheduled_total_amount - right.scheduled_total_amount;
        break;
      default:
        comparison = 0;
    }

    return comparison * direction;
  });
}

function SortHeader({
  active,
  column,
  direction,
  label,
  onSort,
}: {
  active: boolean;
  column: SortColumn;
  direction: SortDirection;
  label: string;
  onSort: (column: SortColumn) => void;
}) {
  return (
    <button
      className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 transition hover:text-white"
      onClick={() => onSort(column)}
      type="button"
    >
      {label}
      <span aria-hidden="true" className="text-[9px]">
        {active ? (direction === "asc" ? "▲" : "▼") : "⇅"}
      </span>
    </button>
  );
}

export function BookingListView({
  bookings,
  loading = false,
  selectedBookingId,
  onSelectBooking,
}: BookingListViewProps) {
  const { t } = useAppPreferences();
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<ListSort>({ column: "start", direction: "asc" });

  const visibleBookings = useMemo(() => {
    const filtered = filterBookings(bookings, search, t);
    return sortBookings(filtered, sort);
  }, [bookings, search, sort, t]);

  const handleSort = (column: SortColumn) => {
    setSort((current) =>
      current.column === column
        ? { column, direction: current.direction === "asc" ? "desc" : "asc" }
        : { column, direction: "asc" },
    );
  };

  return (
    <div className="grid gap-3">
      <input
        aria-label={t("Search by customer, unit, status…")}
        className="ops-input w-full"
        onChange={(event) => setSearch(event.target.value)}
        placeholder={t("Search by customer, unit, status…")}
        type="search"
        value={search}
      />

      {visibleBookings.length === 0 ? (
        <div aria-live="polite" className="ops-empty grid justify-items-center gap-3 px-4 py-10 text-center">
          {loading && bookings.length === 0 ? t("Loading bookings for the current calendar window...") : search
            ? t("No bookings match your search.")
            : t("No bookings in the current range.")}
          {search && !(loading && bookings.length === 0) ? <button className="ops-button min-h-11" data-variant="secondary" type="button" onClick={() => setSearch("")}>{t("Clear search")}</button> : null}
        </div>
      ) : (
        <div className="grid gap-1.5">
          <div className="hidden grid-cols-[1fr_1fr_120px_110px_100px] gap-2 px-[0.4rem] sm:grid">
            <SortHeader
              active={sort.column === "start"}
              column="start"
              direction={sort.direction}
              label={t("Scheduled")}
              onSort={handleSort}
            />
            <SortHeader
              active={sort.column === "customer"}
              column="customer"
              direction={sort.direction}
              label={t("Customer")}
              onSort={handleSort}
            />
            <SortHeader
              active={sort.column === "unit"}
              column="unit"
              direction={sort.direction}
              label={t("Unit")}
              onSort={handleSort}
            />
            <SortHeader
              active={sort.column === "status"}
              column="status"
              direction={sort.direction}
              label={t("Status")}
              onSort={handleSort}
            />
            <SortHeader
              active={sort.column === "total"}
              column="total"
              direction={sort.direction}
              label={t("Total")}
              onSort={handleSort}
            />
          </div>

          {visibleBookings.map((booking) => {
            const isSelected = booking.id === selectedBookingId;
            // Terminal rows are history: keep them findable but visually
            // quiet so the actionable queue owns the attention.
            const isTerminal = !isActionableBookingStatus(booking.status);

            return (
              <button
                aria-pressed={isSelected}
                className={`ops-record ops-record-dense ops-interactive-record text-start transition ${
                  isSelected
                    ? "border-[rgba(91,44,255,0.45)] bg-[rgba(91,44,255,0.12)]"
                    : "hover:border-[rgba(91,44,255,0.30)]"
                }${isTerminal && !isSelected ? " opacity-55" : ""}`}
                data-active={isSelected}
                key={booking.id}
                onClick={() => onSelectBooking(booking.id)}
                type="button"
              >
                <div className="grid gap-2 sm:grid-cols-[1fr_1fr_120px_110px_100px]">
                  <div className="grid min-w-0 gap-0.5">
                    <span className="truncate text-xs text-slate-400 sm:hidden">
                      {t("Scheduled")}
                    </span>
                    <span className="truncate text-sm text-slate-200">
                      {formatBookingSchedule(booking)}
                    </span>
                  </div>

                  <div className="grid min-w-0 gap-0.5">
                    <span className="truncate text-xs text-slate-400 sm:hidden">
                      {t("Customer")}
                    </span>
                    <span className="truncate text-sm font-medium text-white">
                      {booking.customer_name}
                    </span>
                    <span className="truncate text-xs text-slate-400">
                      {getOperationalContact(booking.customer_phone, t("No contact"))}
                    </span>
                  </div>

                  <div className="grid min-w-0 gap-0.5">
                    <span className="truncate text-xs text-slate-400 sm:hidden">
                      {t("Unit")}
                    </span>
                    <span className="truncate text-sm text-slate-300">
                      {booking.unit_name}
                    </span>
                  </div>

                  <div className="grid min-w-0 gap-0.5">
                    <span className="truncate text-xs text-slate-400 sm:hidden">
                      {t("Status")}
                    </span>
                    <span
                      className={`w-fit rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${getBookingStatusTone(
                        booking.status,
                      )}`}
                    >
                      {t(getBookingStatusLabel(booking.status))}
                    </span>
                  </div>

                  <div className="grid min-w-0 gap-0.5">
                    <span className="truncate text-xs text-slate-400 sm:hidden">
                      {t("Total")}
                    </span>
                    <span className="truncate text-sm text-slate-200">
                      {formatPricingAmount(booking.scheduled_total_amount)}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
